import { mapState } from 'vuex';
import moment from 'moment';
import InputRulesMixin from '@/mixins/input-rules';
import DateTimeMixin from '@/mixins/date-time';
import { getTimeArrayFromRangeKey } from './pickerParseRangeValues';
export default {
  name: 'CoreDateTimePicker',
  mixins: [InputRulesMixin, DateTimeMixin],
  props: {
    from: {
      type: Number,
      default: null,
      required: false
    },
    to: {
      type: Number,
      default: null,
      required: false
    },
    allowNull: {
      type: Boolean,
      default: false,
      required: false
    },
    isRange: {
      type: Boolean,
      default: false,
      required: false
    },
    initialRange: {
      type: String,
      default: 'now-10m'
    },
    dateOnly: {
      type: Boolean,
      default: false,
      required: false
    },
    futureRange: {
      type: Boolean,
      default: false,
      required: false
    },
    defaultExpand: {
      type: Boolean,
      default: false,
      required: false
    },
    hideBorderOnCollapse: {
      type: Boolean,
      default: false,
      required: false
    },
    expandIcon: {
      type: String,
      default: 'fal fa-angle-down',
      required: false
    }
  },
  data: function () {
    return {
      expanded: [],
      menu: false,
      startDate: null,
      endDate: null,
      isDatePickerFormValid: true,
      inputValidationRules: {
        validDate: {},
        validRangeStart: value => this.validateInputRange(value, undefined) || this.$i18n_t('Core.DateTimePicker.errorHintStartAfterEnd'),
        validRangeEnd: value => this.validateInputRange(undefined, value) || this.$i18n_t('Core.DateTimePicker.errorHintEndBeforeStart')
      }
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color']),
    mathOp() {
      return this.futureRange ? '+' : '-';
    },
    expansionPanelsClass() {
      return this.hideBorderOnCollapse ? '' : 'kpi-card-text-01';
    }
  },
  watch: {
    initialRange() {
      this.startDate = parseInt(this.from, 10) || (this.allowNull ? null : getTimeArrayFromRangeKey(this.initialRange)[0]);
      this.endDate = parseInt(this.to, 10) || (this.allowNull ? null : getTimeArrayFromRangeKey(this.initialRange)[1]);
    },
    from() {
      this.startDate = parseInt(this.from, 10) || (this.allowNull ? null : getTimeArrayFromRangeKey(this.initialRange)[0]);
    },
    to() {
      this.endDate = parseInt(this.to, 10) || (this.allowNull ? null : getTimeArrayFromRangeKey(this.initialRange)[1]);
    },
    defaultExpand() {
      this.expanded = this.defaultExpand ? 0 : [];
    }
  },
  mounted() {
    this.startDate = parseInt(this.from, 10) || null;
    this.endDate = parseInt(this.to, 10) || null;
    this.expanded = this.defaultExpand ? 0 : [];
    this.init();
  },
  methods: {
    getExpandIcon(attr) {
      return attr === 'true' ? 'fal fa-angle-up' : 'fal fa-angle-down';
    },
    init() {
      moment.locale(this.$store.state.app.language || window.navigator.userLanguage || window.navigator.language);
      if (this.startDate === null || this.isRange && this.endDate === null) {
        const times = getTimeArrayFromRangeKey(this.initialRange);
        this.startDate = this.startDate || (this.allowNull ? null : times[0]);
        this.endDate = this.endDate || (this.allowNull ? null : times[1]);
      }
    },
    getIsoDateStringFromUnix(unixTimestamp) {
      return moment(unixTimestamp * 1000).format('YYYY-MM-DD');
    },
    getUnixFromIsoDate(isoDateString) {
      return moment(isoDateString).unix();
    },
    getUnixFromLocaleDate(dateLocaleString) {
      return moment(dateLocaleString, this.dateOnly ? 'll' : 'lll', this.getLocale()).unix();
    },
    getLocale() {
      return this.$store.state.app.language || window.navigator.userLanguage || window.navigator.language;
    },
    getLocaleDateFromUnix(unixTimestamp) {
      if (unixTimestamp === null) {
        return '';
      }
      return moment(unixTimestamp * 1000).locale(this.getLocale()).format(this.dateOnly ? 'll' : 'lll');
    },
    handleQuickRangeClick(rangeKey, soFar) {
      const times = getTimeArrayFromRangeKey(rangeKey, soFar);
      this.startDate = times[0];
      this.endDate = times[1];
      this.handleApplyClick();
    },
    handleApplyClick() {
      if (this.dateTimeIsValid(this.startDate) && this.dateTimeIsValid(this.endDate) && this.validateInputRange(this.startDate, this.endDate)) {
        this.$emit('date:picked', {
          from: this.startDate,
          to: this.isRange ? this.endDate : undefined
        });
        this.expanded = [];
      }
    },
    handleStartDateChange(value) {
      const unixTimestamp = moment(value, this.dateOnly ? 'll' : 'lll', this.getLocale()).unix();
      if (!isNaN(unixTimestamp)) {
        this.startDate = unixTimestamp;
        this.$refs.formDatePicker.validate();
      }
    },
    handleEndDateChange(inputDateString) {
      const timestamp = this.getUnixFromLocaleDate(inputDateString);
      if (!isNaN(timestamp)) {
        this.endDate = timestamp;
        this.$refs.formDatePicker.validate();
      }
    },
    handleStartDatePicked(isoDate) {
      this.startDate = moment(isoDate).unix();
      this.$refs.formDatePicker.validate();
    },
    handleEndDatePicked(isoDate) {
      this.endDate = moment(isoDate).endOf('day').unix();
      this.$refs.formDatePicker.validate();
    },
    validateInputRange(startDate = this.startDate, endDate = this.endDate) {
      // ignore rangecheck if component is not working with time ranges
      if (!this.isRange) {
        return true;
      }
      const startTS = isNaN(startDate) ? this.getUnixFromLocaleDate(startDate) : startDate;
      const endTS = isNaN(endDate) ? this.getUnixFromLocaleDate(endDate) : endDate;
      return moment(startTS * 1000).isBefore(endTS * 1000);
    }
  }
};